import { useEffect, useState } from "react";
import { CalendarHeart, Loader2, Plus, Trash2, ShieldPlus, Baby, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PROTOCOLS = ["Natural Cycle", "Letrozole", "Clomiphene", "Gonadotropins", "IVF Referral"];
const PROCEDURES = ["", "Timed Intercourse", "IUI", "IVF"];
const OUTCOMES = ["Pending", "Pregnant", "Negative", "Biochemical", "Miscarriage"];
const OUTCOME_STYLES = {
    Pending: "bg-slate-100 text-slate-600 border-slate-200",
    Pregnant: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Negative: "bg-red-50 text-red-700 border-red-200",
    Biochemical: "bg-amber-50 text-amber-700 border-amber-200",
    Miscarriage: "bg-red-50 text-red-700 border-red-200",
};

const EMPTY_CYCLE_ENTRY = { start_date: "", cycle_length: "", period_length: "", flow: "", symptoms: "", notes: "" };
const EMPTY_DEVICE = { method: "", start_date: new Date().toISOString().slice(0, 10), end_date: "", provider: "", lot_no: "", notes: "" };
const EMPTY_FERTILITY = { cycle_start: "", protocol: "Natural Cycle", medication: "", trigger_date: "", procedure: "", procedure_date: "", outcome: "Pending", notes: "" };

function MenstrualPanel({ patientId }) {
    const [data, setData] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState(EMPTY_CYCLE_ENTRY);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/patients/${patientId}/cycles`);
            setData(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };
    useEffect(() => { load(); }, [patientId]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/patients/${patientId}/cycles`, {
                ...form,
                cycle_length: form.cycle_length ? Number(form.cycle_length) : null,
                period_length: form.period_length ? Number(form.period_length) : null,
            });
            toast.success("Cycle entry saved");
            setShowAdd(false);
            setForm(EMPTY_CYCLE_ENTRY);
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const remove = async (en) => {
        if (!window.confirm(`Delete cycle entry of ${en.start_date}?`)) return;
        try {
            await api.delete(`/cycles/${en.id}`);
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>;
    const s = data.summary;

    return (
        <div className="space-y-4" data-testid="menstrual-panel">
            <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><CalendarHeart className="h-4 w-4 text-[#E06D1F]" /> Menstrual Cycle Tracker</h3>
                <Button size="sm" data-testid="add-cycle-btn" onClick={() => setShowAdd(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5"><Plus className="h-4 w-4" /> Add Period</Button>
            </div>

            {s ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cycle-summary">
                    <div className="rounded-xl border border-orange-200 bg-[#FFF4EC] p-4">
                        <p className="text-[11px] font-medium uppercase tracking-wider text-[#C2410C]">Next Period</p>
                        <p className="font-heading text-xl font-extrabold text-slate-900 mt-1" data-testid="cycle-next-due">{s.next_due || "—"}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{s.days_to_next == null ? "" : s.days_to_next < 0 ? `${Math.abs(s.days_to_next)}d overdue` : s.days_to_next === 0 ? "Expected today" : `in ${s.days_to_next} days`}</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Est. Ovulation</p>
                        <p className="font-heading text-xl font-extrabold text-slate-900 mt-1" data-testid="cycle-ovulation">{s.ovulation || "—"}</p>
                        <p className="text-xs text-slate-500 mt-0.5">fertile window ±3 days</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Avg Cycle</p>
                        <p className="font-heading text-xl font-extrabold text-slate-900 mt-1">{s.avg_cycle} days</p>
                        <p className="text-xs text-slate-500 mt-0.5">period {s.avg_period} days</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Last Period</p>
                        <p className="font-heading text-xl font-extrabold text-slate-900 mt-1">{s.last_period}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{data.entries.length} entries logged</p>
                    </div>
                </div>
            ) : (
                <p className="text-sm text-slate-500 rounded-xl border border-slate-200 bg-white p-5" data-testid="cycle-empty">No cycle data yet. Log period start dates to get next-period and ovulation predictions.</p>
            )}

            {data.entries.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="cycle-history-card">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                            <th className="px-4 py-2.5">Period Start</th><th className="px-4 py-2.5">Cycle</th><th className="px-4 py-2.5">Period</th><th className="px-4 py-2.5 hidden sm:table-cell">Flow</th><th className="px-4 py-2.5 hidden md:table-cell">Symptoms</th><th className="px-4 py-2.5"></th>
                        </tr></thead>
                        <tbody>
                            {data.entries.map((en) => (
                                <tr key={en.id} data-testid={`cycle-row-${en.id}`} className="border-t border-slate-100">
                                    <td className="px-4 py-2.5 font-medium text-slate-900">{en.start_date}</td>
                                    <td className="px-4 py-2.5 text-slate-600">{en.cycle_length ? `${en.cycle_length}d` : "—"}</td>
                                    <td className="px-4 py-2.5 text-slate-600">{en.period_length ? `${en.period_length}d` : "—"}</td>
                                    <td className="px-4 py-2.5 hidden sm:table-cell text-slate-600">{en.flow || "—"}</td>
                                    <td className="px-4 py-2.5 hidden md:table-cell text-slate-600 max-w-[200px] truncate">{en.symptoms || "—"}</td>
                                    <td className="px-4 py-2.5 text-right"><Button size="sm" variant="ghost" data-testid={`cycle-delete-${en.id}`} onClick={() => remove(en)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="max-w-md" data-testid="cycle-add-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Log Period</DialogTitle>
                        <DialogDescription>Cycle/period lengths improve prediction accuracy.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div className="space-y-1.5"><Label>Period Start Date *</Label><Input data-testid="cycle-start-input" type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Cycle Length (days)</Label><Input data-testid="cycle-length-input" type="number" min="15" max="90" value={form.cycle_length} onChange={(e) => setForm({ ...form, cycle_length: e.target.value })} placeholder="28" /></div>
                            <div className="space-y-1.5"><Label>Period Length (days)</Label><Input data-testid="period-length-input" type="number" min="1" max="15" value={form.period_length} onChange={(e) => setForm({ ...form, period_length: e.target.value })} placeholder="5" /></div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Flow</Label>
                            <select data-testid="cycle-flow-select" value={form.flow} onChange={(e) => setForm({ ...form, flow: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                <option value="">Select</option>
                                {["Light", "Moderate", "Heavy", "Spotting"].map((f) => <option key={f} value={f}>{f}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5"><Label>Symptoms</Label><Input data-testid="cycle-symptoms-input" value={form.symptoms} onChange={(e) => setForm({ ...form, symptoms: e.target.value })} placeholder="Cramps, PMS, clots..." /></div>
                        <Button data-testid="cycle-submit-btn" type="submit" disabled={saving} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Entry</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ContraceptionPanel({ patientId }) {
    const [data, setData] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState(EMPTY_DEVICE);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/patients/${patientId}/contraception`);
            setData(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };
    useEffect(() => { load(); }, [patientId]);

    const save = async (e) => {
        e.preventDefault();
        if (!form.method) { toast.error("Select a method"); return; }
        setSaving(true);
        try {
            await api.post(`/patients/${patientId}/contraception`, form);
            toast.success("Contraception record saved");
            setShowAdd(false);
            setForm(EMPTY_DEVICE);
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const markRemoved = async (it) => {
        try {
            await api.put(`/contraception/${it.id}`, { status: "removed" });
            toast.success(`${it.method} marked removed`);
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const remove = async (it) => {
        if (!window.confirm(`Delete ${it.method} record?`)) return;
        try {
            await api.delete(`/contraception/${it.id}`);
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>;
    const active = data.items.filter((i) => i.status === "active");
    const past = data.items.filter((i) => i.status !== "active");

    const dueBadge = (it) => {
        if (it.days_left == null) return null;
        if (it.days_left < 0) return <span className="rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[10px] font-semibold text-red-600" data-testid={`larc-overdue-${it.id}`}>EXPIRED {Math.abs(it.days_left)}d ago</span>;
        if (it.days_left <= 60) return <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Due in {it.days_left}d</span>;
        return <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">{it.days_left}d left</span>;
    };

    return (
        <div className="space-y-4" data-testid="contraception-panel">
            <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><ShieldPlus className="h-4 w-4 text-[#E06D1F]" /> Contraception / LARC</h3>
                <Button size="sm" data-testid="add-contraception-btn" onClick={() => setShowAdd(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5"><Plus className="h-4 w-4" /> Add Method</Button>
            </div>

            {active.length === 0 && past.length === 0 && (
                <p className="text-sm text-slate-500 rounded-xl border border-slate-200 bg-white p-5" data-testid="contraception-empty">No contraception records. Track IUDs, implants and injections with automatic expiry alerts.</p>
            )}

            {active.map((it) => (
                <div key={it.id} data-testid={`larc-card-${it.id}`} className="rounded-xl border border-orange-200 bg-[#FFF4EC] p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <p className="font-heading text-lg font-bold text-slate-900">{it.method} {dueBadge(it)}</p>
                            <p className="text-xs text-slate-600 mt-1">
                                Started: <b>{it.start_date}</b>{it.due_date ? ` · ${it.status === "active" ? "Remove/replace by" : "Ended"}: ${it.due_date}` : ""}
                                {it.provider ? ` · By: ${it.provider}` : ""}{it.lot_no ? ` · Lot: ${it.lot_no}` : ""}
                            </p>
                            {it.notes && <p className="text-xs text-slate-500 mt-1">{it.notes}</p>}
                        </div>
                        <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" data-testid={`larc-remove-btn-${it.id}`} onClick={() => markRemoved(it)} className="text-amber-700 border-amber-200 hover:bg-amber-50">Mark Removed</Button>
                            <Button size="sm" variant="ghost" data-testid={`larc-delete-${it.id}`} onClick={() => remove(it)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                    </div>
                </div>
            ))}

            {past.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="larc-history-card">
                    <div className="px-5 py-3 border-b border-slate-100"><h4 className="text-sm font-semibold text-slate-700">Past Methods</h4></div>
                    <div className="divide-y divide-slate-100">
                        {past.map((it) => (
                            <div key={it.id} data-testid={`larc-past-${it.id}`} className="flex items-center justify-between px-5 py-2.5">
                                <p className="text-sm text-slate-700">{it.method} <span className="text-xs text-slate-400">{it.start_date} → {it.due_date || "—"} · {it.status}</span></p>
                                <Button size="sm" variant="ghost" data-testid={`larc-past-delete-${it.id}`} onClick={() => remove(it)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="max-w-md" data-testid="contraception-add-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Add Contraception</DialogTitle>
                        <DialogDescription>LARC expiry/removal dates auto-compute from the method; override below if needed.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Method *</Label>
                            <select data-testid="larc-method-select" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                <option value="">Select method</option>
                                {data.methods.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Start / Insertion Date *</Label><Input data-testid="larc-start-input" type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label>Removal / Expiry (auto)</Label><Input data-testid="larc-end-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Inserted By</Label><Input data-testid="larc-provider-input" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label>Lot / Batch No</Label><Input data-testid="larc-lot-input" value={form.lot_no} onChange={(e) => setForm({ ...form, lot_no: e.target.value })} /></div>
                        </div>
                        <div className="space-y-1.5"><Label>Notes</Label><Input data-testid="larc-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                        <Button data-testid="larc-submit-btn" type="submit" disabled={saving} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function FertilityPanel({ patientId }) {
    const [cycles, setCycles] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState(EMPTY_FERTILITY);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(null);
    const [bbtRow, setBbtRow] = useState({ date: "", temp: "" });
    const [scanRow, setScanRow] = useState({ date: "", right_ovary: "", left_ovary: "", endometrium: "", comment: "" });

    const load = async () => {
        try {
            const { data } = await api.get(`/patients/${patientId}/fertility`);
            setCycles(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };
    useEffect(() => { load(); }, [patientId]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/patients/${patientId}/fertility`, form);
            toast.success("Treatment cycle started");
            setShowAdd(false);
            setForm(EMPTY_FERTILITY);
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const patch = async (cycle, update, msg) => {
        try {
            await api.put(`/fertility/${cycle.id}`, update);
            if (msg) toast.success(msg);
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const addBbt = async (cycle) => {
        if (!bbtRow.date || !bbtRow.temp) { toast.error("Enter BBT date and temperature"); return; }
        await patch(cycle, { bbt: [...(cycle.bbt || []), { ...bbtRow }] }, "BBT entry added");
        setBbtRow({ date: "", temp: "" });
    };

    const addScan = async (cycle) => {
        if (!scanRow.date) { toast.error("Enter scan date"); return; }
        await patch(cycle, { scans: [...(cycle.scans || []), { ...scanRow }] }, "Follicular scan added");
        setScanRow({ date: "", right_ovary: "", left_ovary: "", endometrium: "", comment: "" });
    };

    const removeCycle = async (c) => {
        if (!window.confirm(`Delete cycle starting ${c.cycle_start}?`)) return;
        try {
            await api.delete(`/fertility/${c.id}`);
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    if (!cycles) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>;

    return (
        <div className="space-y-4" data-testid="fertility-panel">
            <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Baby className="h-4 w-4 text-[#E06D1F]" /> Infertility / Ovulation Tracking</h3>
                <Button size="sm" data-testid="add-fertility-btn" onClick={() => setShowAdd(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5"><Plus className="h-4 w-4" /> New Cycle</Button>
            </div>

            {cycles.length === 0 ? (
                <p className="text-sm text-slate-500 rounded-xl border border-slate-200 bg-white p-5" data-testid="fertility-empty">No treatment cycles yet. Track ovulation induction, BBT, follicular scans and outcomes per cycle.</p>
            ) : (
                cycles.map((c) => (
                    <div key={c.id} data-testid={`fertility-card-${c.id}`} className="rounded-xl border border-slate-200 bg-white shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                            <div>
                                <p className="text-sm font-semibold text-slate-900">
                                    Cycle from {c.cycle_start} <span className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${OUTCOME_STYLES[c.outcome] || OUTCOME_STYLES.Pending}`} data-testid={`fertility-outcome-${c.id}`}>{c.outcome}</span>
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {c.protocol || "Natural"}{c.medication ? ` · ${c.medication}` : ""}{c.procedure ? ` · ${c.procedure}${c.procedure_date ? ` (${c.procedure_date})` : ""}` : ""}{c.trigger_date ? ` · Trigger: ${c.trigger_date}` : ""}
                                </p>
                            </div>
                            <div className="flex gap-1.5 items-center">
                                <select data-testid={`fertility-outcome-select-${c.id}`} value={c.outcome} onChange={(e) => patch(c, { outcome: e.target.value }, "Outcome updated")} className="rounded-lg border border-slate-300 bg-white py-1.5 px-2 text-xs shadow-sm focus:border-orange-500 outline-none">
                                    {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                                <Button size="sm" variant="outline" data-testid={`fertility-expand-${c.id}`} onClick={() => setOpen(open === c.id ? null : c.id)} className="gap-1 text-xs">
                                    {open === c.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} BBT / Scans
                                </Button>
                                <Button size="sm" variant="ghost" data-testid={`fertility-delete-${c.id}`} onClick={() => removeCycle(c)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                        </div>

                        {open === c.id && (
                            <div className="border-t border-slate-100 px-5 py-4 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid={`fertility-detail-${c.id}`}>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">BBT Chart (°F)</p>
                                    <div className="space-y-1.5 max-h-44 overflow-y-auto">
                                        {(c.bbt || []).map((b, i) => (
                                            <p key={i} className="text-xs text-slate-600 flex justify-between border-b border-slate-50 py-1" data-testid={`bbt-row-${c.id}-${i}`}><span>{b.date}</span><span className="font-mono2 font-semibold text-[#C2410C]">{b.temp}°F</span></p>
                                        ))}
                                        {(c.bbt || []).length === 0 && <p className="text-xs text-slate-400">No BBT entries.</p>}
                                    </div>
                                    <div className="mt-2 flex gap-2">
                                        <Input data-testid={`bbt-date-${c.id}`} type="date" value={bbtRow.date} onChange={(e) => setBbtRow({ ...bbtRow, date: e.target.value })} className="h-8 text-xs" />
                                        <Input data-testid={`bbt-temp-${c.id}`} type="number" step="0.1" placeholder="97.8" value={bbtRow.temp} onChange={(e) => setBbtRow({ ...bbtRow, temp: e.target.value })} className="h-8 text-xs w-24" />
                                        <Button size="sm" data-testid={`bbt-add-${c.id}`} onClick={() => addBbt(c)} className="h-8 bg-[#E06D1F] hover:bg-[#B45309] text-white text-xs"><Plus className="h-3.5 w-3.5" /></Button>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Follicular Monitoring (USG)</p>
                                    <div className="space-y-1.5 max-h-44 overflow-y-auto">
                                        {(c.scans || []).map((sc, i) => (
                                            <p key={i} className="text-xs text-slate-600 border-b border-slate-50 py-1" data-testid={`scan-row-${c.id}-${i}`}>
                                                <b>{sc.date}</b> — R: {sc.right_ovary || "—"} · L: {sc.left_ovary || "—"} · ET: {sc.endometrium ? `${sc.endometrium}mm` : "—"}{sc.comment ? ` · ${sc.comment}` : ""}
                                            </p>
                                        ))}
                                        {(c.scans || []).length === 0 && <p className="text-xs text-slate-400">No scans logged.</p>}
                                    </div>
                                    <div className="mt-2 grid grid-cols-5 gap-1.5">
                                        <Input data-testid={`scan-date-${c.id}`} type="date" value={scanRow.date} onChange={(e) => setScanRow({ ...scanRow, date: e.target.value })} className="h-8 text-xs" />
                                        <Input data-testid={`scan-right-${c.id}`} placeholder="R 18mm" value={scanRow.right_ovary} onChange={(e) => setScanRow({ ...scanRow, right_ovary: e.target.value })} className="h-8 text-xs" />
                                        <Input data-testid={`scan-left-${c.id}`} placeholder="L 14mm" value={scanRow.left_ovary} onChange={(e) => setScanRow({ ...scanRow, left_ovary: e.target.value })} className="h-8 text-xs" />
                                        <Input data-testid={`scan-et-${c.id}`} placeholder="ET 8" value={scanRow.endometrium} onChange={(e) => setScanRow({ ...scanRow, endometrium: e.target.value })} className="h-8 text-xs" />
                                        <Button size="sm" data-testid={`scan-add-${c.id}`} onClick={() => addScan(c)} className="h-8 bg-[#E06D1F] hover:bg-[#B45309] text-white text-xs"><Plus className="h-3.5 w-3.5" /></Button>
                                    </div>
                                </div>
                                {c.notes && <p className="lg:col-span-2 text-xs text-slate-500"><Pencil className="inline h-3 w-3 mr-1" />{c.notes}</p>}
                            </div>
                        )}
                    </div>
                ))
            )}

            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="max-w-md" data-testid="fertility-add-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">New Treatment Cycle</DialogTitle>
                        <DialogDescription>Ovulation induction / assisted conception cycle.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Cycle Start (Day 1) *</Label><Input data-testid="fert-start-input" type="date" required value={form.cycle_start} onChange={(e) => setForm({ ...form, cycle_start: e.target.value })} /></div>
                            <div className="space-y-1.5">
                                <Label>Protocol</Label>
                                <select data-testid="fert-protocol-select" value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                    {PROTOCOLS.map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="space-y-1.5"><Label>Medication</Label><Input data-testid="fert-medication-input" value={form.medication} onChange={(e) => setForm({ ...form, medication: e.target.value })} placeholder="Tab Letrozole 5mg D3-D7" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label>Procedure</Label>
                                <select data-testid="fert-procedure-select" value={form.procedure} onChange={(e) => setForm({ ...form, procedure: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                    {PROCEDURES.map((p) => <option key={p} value={p}>{p || "None yet"}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1.5"><Label>Procedure Date</Label><Input data-testid="fert-procdate-input" type="date" value={form.procedure_date} onChange={(e) => setForm({ ...form, procedure_date: e.target.value })} /></div>
                        </div>
                        <div className="space-y-1.5"><Label>Trigger Date (hCG)</Label><Input data-testid="fert-trigger-input" type="date" value={form.trigger_date} onChange={(e) => setForm({ ...form, trigger_date: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Notes</Label><Textarea data-testid="fert-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="AMH, semen analysis, tubal status..." /></div>
                        <Button data-testid="fert-submit-btn" type="submit" disabled={saving} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Start Cycle</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function GynaeTools({ patientId, patient }) {
    const [sub, setSub] = useState("menstrual");
    return (
        <div className="space-y-4" data-testid="gynae-tools-tab">
            <div className="flex gap-1 border-b border-slate-200" data-testid="gynae-subtabs">
                {[["menstrual", "Menstrual Cycles"], ["contraception", "Contraception / LARC"], ["fertility", "Infertility / Ovulation"]].map(([k, l]) => (
                    <button key={k} data-testid={`gynae-subtab-${k}`} onClick={() => setSub(k)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors duration-150 ${sub === k ? "border-[#E06D1F] text-[#C2410C]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
                ))}
            </div>
            {sub === "menstrual" && <MenstrualPanel patientId={patientId} />}
            {sub === "contraception" && <ContraceptionPanel patientId={patientId} />}
            {sub === "fertility" && <FertilityPanel patientId={patientId} />}
        </div>
    );
}
